import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue
import { useRecoilValue } from 'recoil';
// @ts-ignore - TypeScript casing issue
import {
  speakingAudioRecordingsState,
  lessonsBySubcourseAtom,
  courseTreeAtom,
  courseTreeBaselineAtom,
} from '../../../../recoil/atoms';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import { ResultLayout } from '../../../../components/Learner/ResultPage';
import { getResultPracticeContext } from '../../../../utils/practiceUtils';

const SkillResultPageRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // changed by Cursor: read audio from Recoil instead of localStorage
  const audioRecordings = useRecoilValue(speakingAudioRecordingsState);
  const lessonsCache = useRecoilValue(lessonsBySubcourseAtom);
  const courseTree = useRecoilValue(courseTreeAtom);
  const courseTreeBaseline = useRecoilValue(courseTreeBaselineAtom);
  
  // Get result data from location state
  const locationState = location.state;
  
  const {
    language: propLanguage,
    courseType = 'Skill Practice',
    level = 'All Levels',
    score: propScore,
    totalQuestions: propTotalQuestions,
    correctCount: propCorrectCount,
    practiceParams = null,
    apiResponse = null,
    originalQuestions = [],
    userAnswers = [],
    sessionData = null,
    fromPage = '/learner/language-learning',
  } = locationState || {};
  
  // Determine language based on propLanguage, sessionData, or fromPage
  let language = propLanguage || sessionData?.['course-name'] || null;
  
  // If not found, infer from fromPage
  if (!language) {
    if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
      language = 'English language learning';
    } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
      language = 'Japanese language learning';
    } else {
      language = 'German language learning';
    }
  }
  
  useEffect(() => {
    if (!locationState) {
      navigate('/learner/language-learning', { replace: true });
      return;
    }
    
    const hasApiResponse = !!locationState.apiResponse;
    const hasFallbackData = locationState.score !== undefined || 
                            locationState.totalQuestions !== undefined ||
                            locationState.correctCount !== undefined;
    
    if (!hasApiResponse && !hasFallbackData) {
      navigate('/learner/language-learning', { replace: true });
    }
  }, [locationState, navigate]);

  const toDisplay = apiResponse?.['to-display'] || apiResponse?.toDisplay || {};
  const hasApiData = toDisplay && typeof toDisplay === 'object' && toDisplay['session-accuracy'] !== undefined;
  
  const score = hasApiData 
    ? (toDisplay['session-accuracy'] ?? toDisplay.sessionAccuracy ?? propScore ?? 0)
    : (propScore ?? 0);
  
  let totalQuestions = propTotalQuestions ?? 10;
  let correctCount = propCorrectCount ?? 0;
  
  if (hasApiData && toDisplay['soals-stats']) {
    totalQuestions = toDisplay['soals-stats'].length;
    correctCount = Math.round((score / 100) * totalQuestions);
  } else if (hasApiData) {
    correctCount = Math.round((score / 100) * totalQuestions);
  }

  const isDrillVocab = courseType === 'Drill Vocab';

  // changed by Cursor: always use green background (same as subcourse practice)
  useEffect(() => {
    const bg = 'linear-gradient(135deg, #1d4746 0%, #236463 50%, #12766d 100%)';
    document.body.style.background = bg;
    document.body.style.backgroundImage = 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 1px, transparent 1px)';
    document.body.style.backgroundSize = '24px 24px';
    document.documentElement.style.background = bg;
    
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.documentElement.style.background = '';
    };
  }, []);

  const handleBack = () => {
    // Priority 1: Use fromPage from location state (from English Language Learning Page)
    if (fromPage && fromPage !== '/learner/language-learning') {
      console.log('📖 SkillResultPage: Navigating back to fromPage:', fromPage);
      navigate(fromPage);
      return;
    }
    
    // Priority 2: Navigate back to last opened URL
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      console.log('📖 SkillResultPage: Navigating back to lastOpenedUrl:', lastOpenedUrl);
      navigate(lastOpenedUrl);
      return;
    }
    
    // Priority 3: Fallback to fromPage or default
    console.log('📖 SkillResultPage: No lastOpenedUrl found, using fromPage:', fromPage);
    navigate(fromPage);
  };

  const handleContinue = () => {
    if (practiceParams) {
      navigate('/quick-practice', { state: practiceParams });
    } else {
      navigate('/learner/language-learning');
    }
  };

  const handleReview = () => {
    // Combine original questions (with options) with soals-stats (with answers)
    const soalsStats = toDisplay['soals-stats'] || [];
    
    console.log('📊 Skill Review Page - soals-stats:', soalsStats);
    console.log('📊 Skill Review Page - originalQuestions:', originalQuestions);
    console.log('📊 Skill Review Page - userAnswers:', userAnswers);
    
    // changed by Cursor: reorder soals-stats supaya ngikutin urutan originalQuestions (practice order)
    const questionsToProcess = soalsStats.length > 0 
      ? (() => {
          // Build unique soals-stats map by soal-id
          // changed by Cursor: also check drill-id for vocab drill soals-stats
          const soalsStatsMap = new Map();
          soalsStats.forEach((soal) => {
            const soalId = soal['soal-id'] || soal.soalId || soal['drill-id'] || soal.id;
            if (soalId && !soalsStatsMap.has(soalId)) {
              soalsStatsMap.set(soalId, soal);
            }
          });
          
          // Reorder to match originalQuestions order so review matches practice order
          return originalQuestions.map((oq, idx) => {
            const oqId = oq['soal-id'] || oq.soalId || oq['drill-id'] || oq.id;
            return soalsStatsMap.get(oqId) || soalsStats[idx] || oq;
          });
        })()
      : originalQuestions;
    
    const formattedQuestions = questionsToProcess.map((soal, index) => {
      const soalId = soal['soal-id'] || soal.soalId || soal.id;
      let originalQuestion = originalQuestions.find(q => 
        (q['soal-id'] || q.soalId || q.id) === soalId
      );
      
      if (!originalQuestion && originalQuestions[index]) {
        originalQuestion = originalQuestions[index];
      }
      
      const correctAnswer = soalsStats.length > 0
        ? (soal.answers || soal['correct-answer'] || soal.correct_answer || soal.correctAnswer)
        : (originalQuestion?.answers || originalQuestion?.['correct-answer'] || originalQuestion?.correct_answer || originalQuestion?.correctAnswer);
      
      const userAnswerData = userAnswers[index];
      
      const isCorrect = soalsStats.length > 0 
        ? (soal.accuracy === 100 || soal.accuracy === 1.0 || (soal['is-correct'] ?? soal.is_correct ?? soal.isCorrect ?? false))
        : (userAnswerData?.isCorrect ?? false);
      
      const rawOptions = originalQuestion?.options || originalQuestion?.choices || [];
      const questionText = originalQuestion?.['text-soal'] || originalQuestion?.text || originalQuestion?.question || soal.question || soal.prompt || `Question ${index + 1}`;
      
      const options = rawOptions.map(opt => {
        if (typeof opt === 'string') {
          return opt;
        } else if (opt && typeof opt === 'object') {
          return opt['option-text'] || opt.text || opt.optionText || String(opt);
        }
        return String(opt);
      });
      
      let formattedCorrectAnswer = [];
      let formattedUserAnswer = [];
      
      const questionType = soal.type || originalQuestion?.type || '';
      
      let userAnswer;
      if (questionType === 'pronunciation' || questionType === 'speaking') {
        userAnswer = userAnswerData;
      } else {
        userAnswer = userAnswerData?.['user-answer'] ?? userAnswerData?.userAnswer ?? userAnswerData?.answer ?? userAnswerData;
      }
      
      if (questionType === 'sequencing' || questionType === 'Sequencing') {
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : [];
        formattedUserAnswer = Array.isArray(userAnswer) ? userAnswer : [];
      } else if (questionType === 'pairing' || questionType === 'pair') {
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : [];
        formattedUserAnswer = userAnswer;
      } else if (questionType.includes('pilgan') || questionType.includes('sc') || questionType.includes('mc')) {
        if (Array.isArray(correctAnswer)) {
          formattedCorrectAnswer = correctAnswer.map(idx => options[idx] || `Option ${idx}`);
        } else if (typeof correctAnswer === 'number' && options[correctAnswer]) {
          formattedCorrectAnswer = [options[correctAnswer]];
        }
        
        if (Array.isArray(userAnswer)) {
          formattedUserAnswer = userAnswer.map(idx => options[idx] || `Option ${idx}`);
        } else if (typeof userAnswer === 'number' || userAnswer === 0) {
          formattedUserAnswer = options[userAnswer] !== undefined ? [options[userAnswer]] : [`Option ${userAnswer}`];
        }
      } else if (questionType.includes('fib') || questionType.includes('fill')) {
        if (Array.isArray(correctAnswer)) {
          formattedCorrectAnswer = correctAnswer;
        }
        if (Array.isArray(userAnswer)) {
          formattedUserAnswer = userAnswer.map((val, idx) => {
            if (typeof val === 'number') {
              return val;
            }
            if (typeof val === 'string' && !isNaN(Number(val)) && val.trim() !== '') {
              return parseInt(val, 10);
            }
            const foundIdx = options.findIndex(opt => opt === val);
            if (foundIdx !== -1) {
              return foundIdx;
            }
            return val;
          });
        } else if (typeof userAnswer === 'number' || userAnswer === 0) {
          formattedUserAnswer = [userAnswer];
        }
      } else if (questionType === 'pronunciation') {
        let assessmentResult = userAnswerData?.assessment || 
                                soal['assessment-result'] || 
                                soal.assessment_result || 
                                soal.assessmentResult;
        
        if (!assessmentResult) {
          try {
            const possibleSoalIds = [
              soalId,
              originalQuestion?.['soal-id'],
              originalQuestion?.id,
              originalQuestion?.soalId,
            ].filter(Boolean);
            
            for (const qId of possibleSoalIds) {
              if (!qId) continue;
              const savedAssessmentKey = `pronunciation-assessment-${qId}`;
              const savedAssessment = localStorage.getItem(savedAssessmentKey);
              if (savedAssessment) {
                assessmentResult = JSON.parse(savedAssessment);
                break;
              }
            }
          } catch (err) {
            console.error('Error loading assessment from localStorage:', err);
          }
        }
        
        // changed by Cursor: read audio from Recoil instead of localStorage (reliable, no quota issues)
        let audioUrl = null;
        const possibleSoalIds = [soalId, originalQuestion?.['soal-id'], originalQuestion?.id, originalQuestion?.soalId].filter(Boolean);
        const possibleIdsAsString = possibleSoalIds.map(String);
        for (const [, rec] of Object.entries(audioRecordings)) {
          if (rec?.soalId && possibleIdsAsString.includes(String(rec.soalId))) {
            audioUrl = rec.audioUrl;
            break;
          }
        }
        if (!audioUrl) {
          audioUrl = audioRecordings[index.toString()]?.audioUrl || null;
        }
        
        formattedUserAnswer = {
          assessment: assessmentResult,
          audioUrl: audioUrl,
          text: userAnswerData?.userAnswer || userAnswerData?.text || originalQuestion?.['text-to-read'] || originalQuestion?.text,
        };
        formattedCorrectAnswer = [];
      } else if (questionType === 'speaking') {
        // changed by Cursor: read audio from Recoil instead of localStorage
        let audioUrl = audioRecordings[index.toString()]?.audioUrl || null;
        if (!audioUrl) {
          for (const [, rec] of Object.entries(audioRecordings)) {
            if (rec?.soalId && String(rec.soalId) === String(soalId)) {
              audioUrl = rec.audioUrl;
              break;
            }
          }
        }
        
        formattedUserAnswer = {
          audioUrl: audioUrl,
          text: soal['user-answer'] || userAnswerData?.userAnswer || originalQuestion?.text,
        };
        formattedCorrectAnswer = [];
      } else {
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : (correctAnswer !== undefined ? [correctAnswer] : []);
        formattedUserAnswer = Array.isArray(userAnswer) ? userAnswer : (userAnswer !== undefined ? [userAnswer] : []);
      }
      
      // changed by Cursor: include passage & translation from sessionData (session-level) for passage type review
      const reviewQuestion = {
        type: questionType,
        originalQuestion: originalQuestion,
        prompt: questionText,
        audioUrl: originalQuestion?.['audio-url'] || originalQuestion?.audio_url || soal['audio-url'] || soal.audio_url,
        options: options,
        correctAnswer: formattedCorrectAnswer,
        userAnswer: formattedUserAnswer,
        isCorrect,
        points: soal.points || (isCorrect ? 100 : 0),
        showIndices: questionType === 'sequencing' || questionType === 'Sequencing',
        correctIndices: soal['correct-indices'] || soal.correct_indices,
        userIndices: soal['user-indices'] || soal.user_indices,
        explanation: soal.explanation || originalQuestion?.explanation || originalQuestion?.notes,
        translation: soal.translation || originalQuestion?.translation || sessionData?.translation,
        passage: originalQuestion?.passage || sessionData?.passage,
        feedback: soal.feedbacks || soal.feedback,
        accuracy: soal.accuracy,
      };
      
      return reviewQuestion;
    });

    console.log('📊 Skill Review Page - formatted questions:', formattedQuestions);

    // changed by Cursor: vocab drill uses separate review page with blue colors
    const reviewRoute = isDrillVocab ? '/learner/vocab-review' : '/learner/review';
    navigate(reviewRoute, {
      state: {
        courseType,
        score,
        currentLevel: toDisplay['level'] ? (toDisplay['level'] + (toDisplay['progress'] || 0) / 100) : 2,
        questions: formattedQuestions,
        userAnswers,
        fromPage: '/learner/skill-result',
        apiResponse,
        resultPageState: {
          language,
          courseType,
          level,
          score,
          totalQuestions,
          correctCount,
          practiceParams,
          apiResponse,
          originalQuestions,
          userAnswers,
          sessionData,
          fromPage,
        },
      },
    });
  };

  if (!locationState) {
    return null;
  }
  
  const hasApiResponse = !!locationState.apiResponse;
  const hasFallbackData = locationState.score !== undefined || 
                          locationState.totalQuestions !== undefined ||
                          locationState.correctCount !== undefined;
  
  if (!hasApiResponse && !hasFallbackData) {
    return null;
  }

  const subcourseId = practiceParams?.subcourseId || sessionData?.['subcourse-id'];
  const cachedLessons = subcourseId ? lessonsCache[subcourseId]?.lessons || [] : [];
  const { subcourseName, lessonName, practiceTypeLabel, subPracticeTypeLabel, subpracticeTypeLabel } = getResultPracticeContext({
    sessionData,
    practiceParams,
    courseType,
    level,
    lessons: cachedLessons,
    courseTree,
    courseTreeBaseline,
    originalQuestions,
  });

  return (
    <ResultLayout
      variant="skill"
      language={language}
      courseType={courseType}
      level={level}
      subcourseName={subcourseName}
      lessonName={lessonName}
      practiceTypeLabel={practiceTypeLabel}
      subPracticeTypeLabel={subPracticeTypeLabel}
      subpracticeTypeLabel={subpracticeTypeLabel}
      score={score}
      totalQuestions={totalQuestions}
      correctCount={correctCount}
      onBack={handleBack}
      onContinue={handleContinue}
      onReview={handleReview}
    />
  );
};

export { SkillResultPageRoute };
export default SkillResultPageRoute;

